import {
  AddGoalProps,
  AddGoalResponse,
  DevelopmentResponse,
  EditGoalProps,
  GoalTimestamp,
  LearningPlan,
  LearningPlanResponse,
} from "../types/api";
import { ApiResponse } from "../types/profile-editing";
import axiosInstance from "../utils/axios";

export const getLearningPlan = async () => {
  try {
    const response = await axiosInstance.get<DevelopmentResponse>(
      "/api/dashboard/learning-plans/"
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const addLearningPlan = async (credentials: LearningPlan) => {
  try {
    const response = await axiosInstance.post<ApiResponse<LearningPlan>>(
      "/api/dashboard/learning-plans/",
      credentials
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const editLearningPlan = async (credentials: LearningPlan) => {
  try {
    const response = await axiosInstance.patch<ApiResponse<LearningPlan>>(
      `/api/dashboard/learning-plans/${credentials.id}/`,
      credentials
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteLearningPlan = async (id: number) => {
  try {
    const response = await axiosInstance.delete<ApiResponse<LearningPlan>>(
      `/api/dashboard/learning-plans/${id}/`
    );
    if (response.status === 204) return;
    return response.data;
  } catch (error) {
    throw error;
  }
};

//===========================GOAL==============================

export const getLearningPlanGoals = async (id: number) => {
  try {
    const response = await axiosInstance.get<LearningPlanResponse>(
      `/api/dashboard/learning-plans/${id}/goals/`
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching development plan:", error);
    throw new Error("Failed to fetch development plan");
  }
};

export const addGoalToCalendar = async ({
  planId,
  credentials,
}: {
  planId: number;
  credentials: AddGoalProps;
}) => {
  const data: AddGoalProps = {
    title: credentials.title,
    description: credentials.description,
    status: credentials.status,
    priority: credentials.priority,
    resource_links: credentials.resource_links,
    deadline: credentials.deadline,
  };
  if (!credentials.deadline) delete data.deadline;

  try {
    const response = await axiosInstance.post<AddGoalResponse>(
      `/api/dashboard/learning-plans/${planId}/goals/`,
      data
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const editCalendarGoal = async ({
  planId,
  credentials,
}: {
  planId: number;
  credentials: EditGoalProps;
}) => {
  const data: AddGoalProps = {
    title: credentials.title,
    description: credentials.description,
    status: credentials.status,
    priority: credentials.priority,
    resource_links: credentials.resource_links,
    deadline: credentials.deadline !== "" ? credentials.deadline : null,
  };

  try {
    const response = await axiosInstance.patch<AddGoalResponse>(
      `/api/dashboard/learning-plans/${planId}/goals/${credentials.id}/`,
      data
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const removeGoalFromCaledar = async ({
  id,
  planId,
}: {
  planId: number;
  id: number;
}) => {
  try {
    const response = await axiosInstance.delete<AddGoalResponse>(
      `/api/dashboard/learning-plans/${planId}/goals/${id}/`
    );
    if (response.status === 204) return;

    return response.data;
  } catch (error) {
    throw error;
  }
};

//===========================TASK================================

export const addTaskToGoal = async ({
  planId,
  goalId,
  credentials,
}: {
  planId: number;
  goalId: number;
  credentials: GoalTimestamp;
}) => {
  try {
    const response = await axiosInstance.post<ApiResponse<GoalTimestamp>>(
      `/api/dashboard/learning-plans/${planId}/goals/${goalId}/tasks/`,
      {
        title: credentials.title,
        description: credentials.description,
        completed: credentials.completed,
        day_from: credentials.day_from,
        day_to: credentials.day_to,
        display_color: credentials.display_color,
        reminder_me: credentials.reminder_me,
      }
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const editTaskInGoal = async ({
  planId,
  goalId,
  taskId,
  credentials,
}: {
  planId: number;
  goalId: number;
  taskId: number;
  credentials: GoalTimestamp;
}) => {
  try {
    const response = await axiosInstance.patch<ApiResponse<GoalTimestamp>>(
      `/api/dashboard/learning-plans/${planId}/goals/${goalId}/tasks/${taskId}/`,
      {
        title: credentials.title,
        description: credentials.description,
        completed: credentials.completed,
        day_from: credentials.day_from,
        day_to: credentials.day_to,
        display_color: credentials.display_color,
        reminder_me: credentials.reminder_me,
      }
    );

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteTaskInGoal = async ({
  planId,
  goalId,
  taskId,
}: {
  planId: number;
  goalId: number;
  taskId: number;
}) => {
  try {
    const response = await axiosInstance.delete(
      `/api/dashboard/learning-plans/${planId}/goals/${goalId}/tasks/${taskId}/`
    );

    if (response.status === 204) return;
    return response.data;
  } catch (error) {
    throw error;
  }
};
